import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { AlertSoundToggle, useAlertWatcher } from "@/components/AlertSound";
import { LogOut, LayoutDashboard, ClipboardList, Utensils, Users, Table2, Settings, Building2 } from "lucide-react";

export default function StaffLayout({ children }) {
  const { user, logout } = useAuth();
  const { t } = useLang();
  const nav = useNavigate();
  useAlertWatcher(user?.email);

  const isAdmin = user?.role === "admin" || user?.role === "owner";
  const isOwner = user?.role === "owner";
  const link = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 border-l-2 transition-colors ${
      isActive
        ? "border-white bg-[#161616] text-white"
        : "border-transparent text-zinc-400 hover:text-white hover:bg-[#141414]"
    }`;

  return (
    <div className="staff-scope min-h-screen flex">
      <aside className="w-56 border-r border-[#262626] hidden md:flex flex-col">
        <div className="px-5 py-6 border-b border-[#262626]">
          <div className="overline text-zinc-500">{t("restaurant_os")}</div>
          <div className="font-condensed text-2xl font-bold tracking-tight mt-1">
            {t("command_label")}
          </div>
        </div>
        <nav className="py-4 flex-1">
          <NavLink to="/staff" end className={link} data-testid="nav-dashboard">
            <LayoutDashboard size={16} /> <span className="font-plex text-sm">{t("requests")}</span>
          </NavLink>
          <NavLink to="/staff/orders" className={link} data-testid="nav-orders">
            <ClipboardList size={16} /> <span className="font-plex text-sm">{t("orders")}</span>
          </NavLink>
          {isAdmin && (
            <>
              <div className="overline text-zinc-600 px-4 pt-6 pb-2">{t("admin")}</div>
              <NavLink to="/admin/tables" className={link} data-testid="nav-tables">
                <Table2 size={16} /> <span className="font-plex text-sm">{t("tables_qr")}</span>
              </NavLink>
              <NavLink to="/admin/menu" className={link} data-testid="nav-menu">
                <Utensils size={16} /> <span className="font-plex text-sm">{t("menu")}</span>
              </NavLink>
              <NavLink to="/admin/users" className={link} data-testid="nav-users">
                <Users size={16} /> <span className="font-plex text-sm">{t("staff")}</span>
              </NavLink>
              <NavLink to="/admin/settings" className={link} data-testid="nav-settings">
                <Settings size={16} /> <span className="font-plex text-sm">{t("settings")}</span>
              </NavLink>
              {isOwner && (
                <NavLink to="/admin/restaurants" className={link} data-testid="nav-restaurants">
                  <Building2 size={16} /> <span className="font-plex text-sm">{t("restaurants")}</span>
                </NavLink>
              )}
            </>
          )}
        </nav>
        <div className="border-t border-[#262626] p-4">
          <div className="text-xs text-zinc-500 font-plex truncate">{user?.email}</div>
          <div className="flex items-center justify-between mt-1">
            <div className="overline text-zinc-600">{user?.role}</div>
            <AlertSoundToggle />
          </div>
          <button
            onClick={async () => { await logout(); nav("/staff-portal"); }}
            data-testid="logout-btn"
            className="mt-3 w-full flex items-center justify-center gap-2 border border-[#262626] py-2 text-sm hover:bg-[#161616] transition-colors font-plex"
          >
            <LogOut size={14} /> {t("sign_out")}
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0">
        <header className="md:hidden border-b border-[#262626] px-5 py-4 flex items-center justify-between">
          <Link to="/staff" className="font-condensed text-xl font-bold">{t("command_label")}</Link>
          <div className="flex items-center gap-3">
            <AlertSoundToggle testId="mobile-sound-toggle-btn" />
            <button onClick={async () => { await logout(); nav("/staff-portal"); }} className="text-xs text-zinc-400" data-testid="mobile-logout-btn">{t("sign_out")}</button>
          </div>
        </header>
        <main className="p-6 md:p-10">{children}</main>
      </div>
    </div>
  );
}
